import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useChatStore } from "@/stores/chat-store";
import { ConversationList } from "./ConversationList";
import { NewChatSearch } from "./NewChatSearch";

// Floating messages panel anchored to the bottom-right. Visibility is owned by
// the topbar MessagesLauncher (toggles chat-store listOpen). Header mirrors
// the open ChatWindow's header (title left, action + dismiss right) so the
// two floating surfaces share a visual contract. The + button swaps the body
// to a people search for starting a new conversation; selecting a person
// opens the chat window and closes this panel.
export function MessagesBanner() {
  const intl = useIntl();
  const setListOpen = useChatStore((s) => s.setListOpen);
  const [view, setView] = useState<"list" | "new">("list");

  const closeLabel = intl.formatMessage({ id: "common.close", defaultMessage: "Close" });
  const newLabel = intl.formatMessage({
    id: "chat.newConversation",
    defaultMessage: "New conversation",
  });
  const backLabel = intl.formatMessage({ id: "agent.back", defaultMessage: "Back to chats" });

  return (
    <div className="pointer-events-auto flex h-[70dvh] w-[calc(100vw-1.5rem)] shrink-0 flex-col overflow-hidden rounded-t-xl border border-b-0 border-ink/10 bg-surface shadow-2xl sm:h-[460px] sm:w-[260px]">
      <div className="flex items-center gap-2 border-b border-ink/10 px-3 py-2">
        {view === "new" ? (
          <button
            type="button"
            onClick={() => setView("list")}
            aria-label={backLabel}
            title={backLabel}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink/55 transition hover:bg-ink/[0.06] hover:text-ink"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.75}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        ) : null}
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
          {view === "new" ? (
            newLabel
          ) : (
            <FormattedMessage id="chat.messages" defaultMessage="Messages" />
          )}
        </span>
        {view === "list" ? (
          <button
            type="button"
            onClick={() => setView("new")}
            aria-label={newLabel}
            title={newLabel}
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink/45 transition hover:bg-ink/[0.06] hover:text-ink"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.75}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
            </svg>
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setListOpen(false)}
          aria-label={closeLabel}
          title={closeLabel}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink/45 transition hover:bg-ink/[0.06] hover:text-ink"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.75}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {view === "new" ? (
        <NewChatSearch onPicked={() => setView("list")} />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <ConversationList />
        </div>
      )}
    </div>
  );
}
